import type { PlaybookSectionNode } from "@repo/app-types/entities/playbook";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Textarea, TextareaVariant } from "@repo/ui/Inputs/textarea";
import { Label } from "@repo/ui/Label/label";
import { linkStyle } from "@repo/ui/Link/index";
import { type FC, useState } from "react";
import { twMerge } from "tailwind-merge";
import { FieldDiffView } from "./fieldDiffView.js";

export interface SectionFormProps {
  section: PlaybookSectionNode;
  liveSection?: PlaybookSectionNode;
  onUpdateSection: (updates: Partial<PlaybookSectionNode>) => void;
  isReadOnly?: boolean;
}

type FieldTab = "edit" | "diff";

export const SectionForm: FC<SectionFormProps> = ({
  section,
  liveSection,
  onUpdateSection,
  isReadOnly = false,
}) => {
  const [nameTab, setNameTab] = useState<FieldTab>("edit");
  const [descriptionTab, setDescriptionTab] = useState<FieldTab>("edit");

  const liveName = liveSection?.name ?? "";
  const liveDescription = liveSection?.description ?? "";
  const currentDescription = section.description ?? "";

  const showNameDiffToggle =
    liveSection !== undefined && liveName !== section.name;
  const showDescriptionDiffToggle =
    liveSection !== undefined && liveDescription !== currentDescription;

  return (
    <div className="space-y-4">
      <div>
        <div className="mb-1 flex min-h-8 items-center justify-between">
          <Label size={Size.SMALL}>Name</Label>
          {showNameDiffToggle ? (
            <Button
              variant={ButtonVariant.Tertiary}
              size={Size.MEDIUM}
              className={twMerge("cursor-pointer pr-0", linkStyle())}
              onClick={() => {
                setNameTab(nameTab === "edit" ? "diff" : "edit");
              }}
            >
              {nameTab === "edit" ? "See changes" : "Edit"}
            </Button>
          ) : null}
        </div>
        {showNameDiffToggle && nameTab === "diff" ? (
          <FieldDiffView savedValue={liveName} editedValue={section.name} />
        ) : (
          <Input
            value={section.name}
            onChange={(event) => {
              onUpdateSection({ name: event.target.value });
            }}
            placeholder="Introduction and compliance disclaimers"
            isDisabled={isReadOnly}
            maxLength={300}
          />
        )}
      </div>

      <div>
        <div className="mb-1 flex min-h-8 items-center justify-between">
          <Label size={Size.SMALL}>Description</Label>
          {showDescriptionDiffToggle ? (
            <Button
              variant={ButtonVariant.Tertiary}
              size={Size.MEDIUM}
              className={twMerge("cursor-pointer pr-0", linkStyle())}
              onClick={() => {
                setDescriptionTab(descriptionTab === "edit" ? "diff" : "edit");
              }}
            >
              {descriptionTab === "edit" ? "See changes" : "Edit"}
            </Button>
          ) : null}
        </div>
        {showDescriptionDiffToggle && descriptionTab === "diff" ? (
          <FieldDiffView
            savedValue={liveDescription}
            editedValue={currentDescription}
          />
        ) : (
          <Textarea
            value={currentDescription}
            onChange={(newValue) => {
              onUpdateSection({ description: newValue });
            }}
            placeholder="Describe this section..."
            variant={TextareaVariant.DEFAULT}
            className="w-full"
            isDisabled={isReadOnly}
            maxLength={2000}
          />
        )}
        <p className="mt-2 text-sm text-gray-600">
          (Optional) This description helps the AI grader understand when to
          grade or skip this section.
        </p>
      </div>
    </div>
  );
};
